import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CheckCircle, Star, AlertCircle } from 'lucide-react';
import { useLanguage } from '../LanguageContext';

const Products = () => {
  const { t } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Define products with translation keys and exact specifications
  const products = [
    {
      id: 1,
      nameKey: 'kitDebutantName',
      descriptionKey: 'kitDebutantDesc',
      price: 289.99,
      category: 'kit',
      image: 'https://images.unsplash.com/photo-1558002038-1055907df827?q=80&w=1470',
      popular: false,
      featuresKeys: [
        'item4SmartBulbs',
        'item2SmartPlugs',
        'item2MotionSensors',
        'item1Hub',
        'installationIncluded'
      ]
    },
    {
      id: 2,
      nameKey: 'kitIntermediateName',
      descriptionKey: 'kitIntermediateDesc',
      price: 519.99,
      category: 'kit',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?q=80&w=1470',
      popular: true,
      featuresKeys: [
        'item8SmartBulbs',
        'item3SmartPlugs',
        'item3MotionSensors',
        'item1Hub',
        'item1RemoteControl',
        'item1IndoorCamera',
        'installationIncluded'
      ]
    },
    {
      id: 3,
      nameKey: 'kitCompletoName',
      descriptionKey: 'kitCompletoDesc',
      price: 659.99,
      category: 'kit',
      image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=1475',
      popular: false,
      featuresKeys: [
        'item12SmartBulbs',
        'item4SmartPlugs',
        'item4MotionSensors',
        'item1Hub',
        'item1RemoteControl',
        'item1IndoorCamera',
        'installationIncluded'
      ]
    },
    {
      id: 4,
      nameKey: 'smartLockIndividualName',
      descriptionKey: 'smartLockIndividualDesc',
      price: 329.99,
      category: 'accessory',
      image: 'https://images.unsplash.com/photo-1558002038-1055907df827?q=80&w=1470',
      popular: false,
      installationNotIncluded: true,
      featuresKeys: [
        'keylessEntry',
        'remoteAccess',
        'activityLog',
        'easyInstall'
      ]
    }
  ];

  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter(p => p.category === selectedCategory);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="produits" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="bg-blue-100 text-blue-700 mb-4">{t('ourProducts')}</Badge>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t('productsTitle')}
          </h2>
          <p className="text-xl text-gray-600">
            {t('productsSubtitle')}
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex justify-center gap-4 mb-12">
          <Button
            onClick={() => setSelectedCategory('all')}
            variant={selectedCategory === 'all' ? 'default' : 'outline'}
            className={selectedCategory === 'all' ? 'bg-blue-600 hover:bg-blue-700' : 'border-blue-600 text-blue-600 hover:bg-blue-50'}
          >
            {t('all')}
          </Button>
          <Button
            onClick={() => setSelectedCategory('kit')}
            variant={selectedCategory === 'kit' ? 'default' : 'outline'}
            className={selectedCategory === 'kit' ? 'bg-blue-600 hover:bg-blue-700' : 'border-blue-600 text-blue-600 hover:bg-blue-50'}
          >
            {t('completeKits')}
          </Button>
          <Button
            onClick={() => setSelectedCategory('accessory')}
            variant={selectedCategory === 'accessory' ? 'default' : 'outline'}
            className={selectedCategory === 'accessory' ? 'bg-blue-600 hover:bg-blue-700' : 'border-blue-600 text-blue-600 hover:bg-blue-50'}
          >
            {t('accessories')}
          </Button>
        </div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {filteredProducts.map((product, index) => (
            <Card
              key={product.id}
              className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-blue-200 relative overflow-hidden"
              style={{
                animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
              }}
            >
              {product.popular && (
                <div className="absolute top-4 right-4 z-10">
                  <Badge className="bg-blue-600 text-white">
                    <Star className="w-3 h-3 mr-1" fill="currentColor" />
                    {t('popular')}
                  </Badge>
                </div>
              )}

              <div className="relative overflow-hidden h-64">
                <img
                  src={product.image}
                  alt={t(product.nameKey)}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>

              {/* Image Disclaimer */}
              <div className="px-4 pt-3 pb-1">
                <p className="text-xs text-gray-500 italic text-center">
                  {t('imageDisclaimer')}
                </p>
              </div>

              <CardHeader>
                <CardTitle className="text-2xl text-gray-900">{t(product.nameKey)}</CardTitle>
                <CardDescription className="text-gray-600">{t(product.descriptionKey)}</CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="flex items-baseline">
                  <span className="text-3xl font-bold text-blue-600">${product.price}</span>
                  <span className="text-gray-500 ml-2">CAD</span>
                </div>

                {/* Installation Warning for Smart Lock */}
                {product.installationNotIncluded && (
                  <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-3 flex items-start space-x-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm font-semibold text-yellow-800">
                      {t('installationNotIncluded')}
                    </span>
                  </div>
                )}

                <ul className="space-y-2">
                  {product.featuresKeys.map((featureKey, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      <span>{t(featureKey)}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter>
                <Button 
                  onClick={scrollToContact}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {t('orderNow')}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Custom Kits Section */}
        <div className="max-w-4xl mx-auto bg-gradient-to-r from-blue-600 to-blue-400 rounded-3xl p-12 text-white text-center shadow-2xl">
          <h3 className="text-3xl font-bold mb-4">{t('customKitsTitle')}</h3>
          <p className="text-lg mb-2">{t('customKitsSubtitle')}</p>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
            {t('customKitsDescription')}
          </p>
          <Button
            onClick={scrollToContact}
            size="lg"
            className="bg-white text-blue-600 hover:bg-blue-50"
          >
            {t('requestCustomQuote')}
          </Button>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default Products;
